import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'

@Component({
  selector: 'app-priority-list-pagination',
  standalone: true,
  template: `
    <div class="pagination">
      <span>{{ loadedCount }} / {{ totalCount }} 件</span>
      <!-- aria-live は常設要素の中身更新でないと読み上げが安定しないため、@if は内側に置く -->
      <span class="loading-indicator" aria-live="polite">
        @if (isLoading) {
          読み込み中...
        }
      </span>
      <span
        #loadMoreSentinel
        class="load-more-sentinel"
        aria-hidden="true"
      ></span>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .pagination {
        display: flex;
        gap: 12px;
        align-items: center;
        justify-content: flex-end;
        padding: 16px 0 0;
        color: #64748b;
        font-size: 13px;
        font-weight: 700;
      }

      .loading-indicator {
        color: #2563eb;
      }

      .load-more-sentinel {
        width: 1px;
        height: 1px;
      }
    `,
  ],
})
export class PriorityListPaginationComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() hasNextPage = false
  @Input() isLoading = false
  @Input() loadedCount = 0
  @Input() totalCount = 0

  @Output() loadMore = new EventEmitter<void>()

  @ViewChild('loadMoreSentinel', { static: true })
  private loadMoreSentinel?: ElementRef<HTMLElement>

  private observer: IntersectionObserver | null = null
  private isSentinelVisible = false
  private hasEmittedForCurrentIntersection = false

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    if (
      this.loadMoreSentinel == null ||
      typeof IntersectionObserver === 'undefined'
    ) {
      return
    }

    const sentinel = this.loadMoreSentinel.nativeElement
    this.zone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          this.isSentinelVisible = entries.some((entry) => entry.isIntersecting)
          if (!this.isSentinelVisible) {
            this.hasEmittedForCurrentIntersection = false
          }
          this.emitLoadMoreIfNeeded()
        },
        { rootMargin: '160px 0px' },
      )
      this.observer.observe(sentinel)
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['hasNextPage']) {
      if (!this.hasNextPage) {
        this.hasEmittedForCurrentIntersection = false
      } else {
        this.emitLoadMoreIfNeeded()
      }
    }

    // 追記直後は番兵が画面外に出ていても IntersectionObserver の再発火（非同期）前で
    // isSentinelVisible が true のまま残り、最大1ページ余分に読み込むことがある。
    // 連鎖はせず実害も小さいため、画面が大きい環境での連続読み込みを優先して許容する。
    if (changes['isLoading'] && !this.isLoading) {
      this.hasEmittedForCurrentIntersection = false
      this.emitLoadMoreIfNeeded()
    }
  }

  ngOnDestroy(): void {
    this.observer?.disconnect()
  }

  private emitLoadMoreIfNeeded(): void {
    if (
      !this.isSentinelVisible ||
      !this.hasNextPage ||
      this.isLoading ||
      this.hasEmittedForCurrentIntersection
    ) {
      return
    }

    this.hasEmittedForCurrentIntersection = true
    this.zone.run(() => {
      this.loadMore.emit()
    })
  }
}
